import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/i18n/LanguageContext';
import { getTransactions, deleteTransaction, updateTransaction, createCurrencyExchange } from '@/services/api';
import { formatCurrency as formatCurrencyIntl } from '@/lib/currency';
import ReceiptUploader from '@/components/ReceiptUploader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarPicker } from '@/components/ui/calendar';
import { format, parseISO, isValid, startOfMonth, endOfMonth, subMonths, subDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import {
  TrendingUp, TrendingDown, Trash2, LogOut, Search,
  LayoutDashboard, Receipt, Settings, ShieldCheck, Loader2, Filter, Menu, X,
  Pencil, CalendarIcon, XCircle, Wallet, PiggyBank, Tag, Repeat, BarChart3, Upload, Paperclip,
  Image as ImageIcon, Target, ArrowLeftRight, DollarSign
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const EXPENSE_CATEGORIES = ['Alimentación', 'Transporte', 'Vivienda', 'Servicios', 'Entretenimiento', 'Salud', 'Educación', 'Otros'];
const INCOME_CATEGORIES = ['Salario', 'Freelance', 'Inversiones', 'Ventas', 'Otros'];
const TRANSFER_CATEGORY = 'Transferencia';
const normalizeTransactionType = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  const aliases = {
    income: 'income',
    ingreso: 'income',
    receita: 'income',
    renda: 'income',
    expense: 'expense',
    gasto: 'expense',
    despesa: 'expense',
    transfer: 'transfer',
    transferencia: 'transfer',
    transferência: 'transfer',
    currency_exchange_out: 'transfer',
    currency_exchange_in: 'transfer',
  };
  return aliases[normalized] || normalized || 'expense';
};
const accountTypeSuffix = (type) => {
  if (type === 'credit_card') return ' · tarjeta';
  if (type === 'investment') return ' · inversiones';
  return '';
};

const parseDate = (str) => {
  if (!str) return null;
  const d = parseISO(str);
  return isValid(d) ? d : null;
};

const TransactionsPage = () => {
  const { user, logout, isAdmin, currency, locale } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [tagFilter, setTagFilter] = useState('all');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [editing, setEditing] = useState(null); // current tx being edited
  const [editForm, setEditForm] = useState(null);
  const [editError, setEditError] = useState('');
  const [saving, setSaving] = useState(false);
  const [exchangeOpen, setExchangeOpen] = useState(false);
  const [exchangeSaving, setExchangeSaving] = useState(false);
  const [exchangeError, setExchangeError] = useState('');
  const [exchangeForm, setExchangeForm] = useState({
    from_account_id: '',
    to_account_id: '',
    source_amount: '',
    target_amount: '',
    iof_amount: '',
    spread_amount: '',
    date: new Date().toISOString().slice(0, 10),
    description: 'Cambio de moneda'
  });

  useEffect(() => {
    fetchTransactions();
    fetchAccounts();
    fetchCategories();
  }, []);

  const fetchTransactions = async () => {
    try {
      const data = await getTransactions();
      setTransactions(data);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setAccounts(await res.json());
    } catch (e) { /* ignore */ }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setAllCategories(await res.json());
    } catch (e) { /* ignore */ }
  };

  const accountMap = useMemo(() => {
    const m = {};
    accounts.forEach(a => { m[a.id] = a; });
    return m;
  }, [accounts]);

  const filteredTransactions = useMemo(() => {
    let filtered = transactions;

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(tx =>
        (tx.category || '').toLowerCase().includes(q) ||
        (tx.description || '').toLowerCase().includes(q)
      );
    }

    if (typeFilter !== 'all') {
      filtered = filtered.filter(tx => normalizeTransactionType(tx.type) === typeFilter);
    }

    if (fromDate) {
      const fromStr = format(fromDate, 'yyyy-MM-dd');
      filtered = filtered.filter(tx => tx.date >= fromStr);
    }
    if (toDate) {
      const toStr = format(toDate, 'yyyy-MM-dd');
      filtered = filtered.filter(tx => tx.date <= toStr);
    }

    if (tagFilter !== 'all') {
      filtered = filtered.filter(tx => Array.isArray(tx.tags) && tx.tags.includes(tagFilter));
    }

    return filtered;
  }, [transactions, searchTerm, typeFilter, fromDate, toDate, tagFilter]);

  // All unique tags from current data set for the filter dropdown
  const availableTags = useMemo(() => {
    const set = new Set();
    transactions.forEach(t => (t.tags || []).forEach(tag => set.add(tag)));
    return Array.from(set).sort();
  }, [transactions]);

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar esta transacción?')) {
      try {
        await deleteTransaction(id);
        fetchTransactions();
      } catch (error) {
        console.error('Error deleting transaction:', error);
      }
    }
  };

  const openEdit = (tx) => {
    if ((tx.tags || []).includes('currency_exchange')) {
      window.alert('Para corregir un cambio de moneda, borra la operación y créala de nuevo.');
      return;
    }
    const txType = normalizeTransactionType(tx.type);
    setEditError('');
    setEditing(tx);
    setEditForm({
      type: txType,
      category: txType === 'transfer' ? TRANSFER_CATEGORY : tx.category,
      amount: String(tx.amount),
      description: tx.description || '',
      date: parseDate(tx.date) || new Date(),
      account_id: tx.account_id || '',
      to_account_id: tx.to_account_id || '',
      notes: tx.notes || '',
      tags: (tx.tags || []).join(', '),
      receipt_base64: tx.receipt_base64 || '',
    });
  };

  const closeEdit = () => {
    setEditing(null);
    setEditForm(null);
    setEditError('');
  };

  const openExchangeDialog = () => {
    const source = accounts.find(acc => acc.currency === currency && acc.type !== 'credit_card') || accounts.find(acc => acc.type !== 'credit_card') || accounts[0];
    const target = accounts.find(acc => acc.currency && acc.currency !== (source?.currency || currency)) || accounts.find(acc => acc.type !== 'credit_card' && acc.id !== source?.id);
    setExchangeForm({
      from_account_id: source?.id || '',
      to_account_id: target?.id || '',
      source_amount: '',
      target_amount: '',
      iof_amount: '',
      spread_amount: '',
      date: new Date().toISOString().slice(0, 10),
      description: 'Cambio de moneda'
    });
    setExchangeError('');
    setExchangeOpen(true);
  };

  const handleSaveExchange = async (e) => {
    e.preventDefault();
    setExchangeError('');
    if (!exchangeForm.from_account_id || !exchangeForm.to_account_id) {
      setExchangeError('Selecciona cuenta origen y cuenta destino.');
      return;
    }
    if (exchangeForm.from_account_id === exchangeForm.to_account_id) {
      setExchangeError('La cuenta origen y la cuenta destino deben ser diferentes.');
      return;
    }
    const payload = {
      from_account_id: exchangeForm.from_account_id,
      to_account_id: exchangeForm.to_account_id,
      source_amount: parseFloat(exchangeForm.source_amount || '0'),
      target_amount: parseFloat(exchangeForm.target_amount || '0'),
      iof_amount: parseFloat(exchangeForm.iof_amount || '0'),
      spread_amount: parseFloat(exchangeForm.spread_amount || '0'),
      date: exchangeForm.date,
      description: exchangeForm.description || 'Cambio de moneda'
    };
    if (payload.source_amount <= 0 || payload.target_amount <= 0) {
      setExchangeError('Los valores debitado y recibido deben ser mayores que cero.');
      return;
    }
    setExchangeSaving(true);
    try {
      await createCurrencyExchange(payload);
      setExchangeOpen(false);
      await Promise.all([fetchTransactions(), fetchAccounts()]);
    } catch (error) {
      console.error('Error creating currency exchange:', error);
      setExchangeError(error?.response?.data?.detail || 'No fue posible guardar el cambio de moneda.');
    } finally {
      setExchangeSaving(false);
    }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editing || !editForm) return;
    setEditError('');
    if (editForm.type === 'transfer') {
      if (!editForm.account_id || !editForm.to_account_id) {
        setEditError('Para guardar como transferencia selecciona cuenta origen y cuenta destino.');
        return;
      }
      if (editForm.account_id === editForm.to_account_id) {
        setEditError('La cuenta origen y la cuenta destino deben ser diferentes.');
        return;
      }
    }
    setSaving(true);
    try {
      await updateTransaction(editing.id, {
        type: editForm.type,
        category: editForm.type === 'transfer' ? TRANSFER_CATEGORY : editForm.category,
        amount: parseFloat(editForm.amount),
        description: editForm.description,
        date: format(editForm.date, 'yyyy-MM-dd'),
        account_id: editForm.account_id || null,
        to_account_id: editForm.type === 'transfer' ? (editForm.to_account_id || null) : null,
        notes: editForm.notes || null,
        tags: (editForm.tags || '').split(',').map(t => t.trim()).filter(Boolean),
        // empty string = clear; null = keep; data URL = set
        receipt_base64: editForm.receipt_base64 === '' && editing.receipt_base64
          ? ''
          : (editForm.receipt_base64 || null),
      });
      closeEdit();
      fetchTransactions();
    } catch (error) {
      console.error('Error updating transaction:', error);
      setEditError(error?.response?.data?.detail || 'No se pudo guardar la transacción. Revisa los datos e intenta nuevamente.');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const formatCurrency = (amount) => formatCurrencyIntl(amount, currency, locale);
  const formatCurrencyFor = (amount, moneyCurrency) => formatCurrencyIntl(amount, moneyCurrency || currency, locale);
  const typeMeta = (type) => {
    const normalizedType = normalizeTransactionType(type);
    if (normalizedType === 'income') return { label: t('income'), icon: TrendingUp, badge: 'badge-income', amountClass: 'text-emerald-400', sign: '+' };
    if (normalizedType === 'transfer') return { label: t('transfer'), icon: ArrowLeftRight, badge: 'bg-sky-500/10 text-sky-300 border border-sky-500/30', amountClass: 'text-sky-300', sign: '' };
    return { label: t('expense'), icon: TrendingDown, badge: 'badge-expense', amountClass: 'text-rose-400', sign: '-' };
  };

  const clearDateRange = () => {
    setFromDate(null);
    setToDate(null);
  };

  const applyThisMonth = () => {
    const now = new Date();
    setFromDate(startOfMonth(now));
    setToDate(endOfMonth(now));
  };
  const applyLastMonth = () => {
    const ref = subMonths(new Date(), 1);
    setFromDate(startOfMonth(ref));
    setToDate(endOfMonth(ref));
  };
  const applyLast7Days = () => {
    const now = new Date();
    setFromDate(subDays(now, 6));
    setToDate(now);
  };

  const totals = useMemo(() => {
    let income = 0;
    let expense = 0;
    for (const tx of filteredTransactions) {
      const txType = normalizeTransactionType(tx.type);
      if (txType === 'income') income += tx.amount;
      else if (txType === 'expense') expense += tx.amount;
    }
    return { income, expense, net: income - expense };
  }, [filteredTransactions]);

  const editCategories = (() => {
    const type = editForm?.type || 'expense';
    if (type === 'transfer') return [TRANSFER_CATEGORY];
    const dyn = allCategories.filter(c => c.type === type && c.is_active).map(c => c.name);
    if (dyn.length > 0) return dyn;
    return type === 'income' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;
  })();
  const editTransferInvalid = editForm?.type === 'transfer' && (
    !editForm.account_id ||
    !editForm.to_account_id ||
    editForm.account_id === editForm.to_account_id
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-violet-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 flex">
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/60" onClick={() => setMobileMenuOpen(false)} />
          <aside className="fixed left-0 top-0 bottom-0 w-72 bg-zinc-900 border-r border-zinc-800 p-6 z-50">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="text-2xl font-black gradient-text">WSFinances</h1>
                <p className="text-zinc-500 text-sm mt-1">Panel de Finanzas</p>
              </div>
              <button onClick={() => setMobileMenuOpen(false)} className="text-zinc-400 hover:text-white">
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="space-y-2">
              <Link to="/dashboard" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <LayoutDashboard className="h-5 w-5" />
                Dashboard
              </Link>
              <Link to="/transactions" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-violet-500/10 text-violet-400 border border-violet-500/20" onClick={() => setMobileMenuOpen(false)}>
                <Receipt className="h-5 w-5" />
                {t('transactions')}
              </Link>
              <Link to="/accounts" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <Wallet className="h-5 w-5" />
                {t('accounts')}
              </Link>
              <Link to="/categories" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <Tag className="h-5 w-5" />
                {t('category')}
              </Link>
              <Link to="/recurring" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <Repeat className="h-5 w-5" />
                Recurrentes
              </Link>
              <Link to="/budgets" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <Target className="h-5 w-5" />
                {t('budgets')}
              </Link>
              <Link to="/goals" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <PiggyBank className="h-5 w-5" />
                {t('goalsAndDebts')}
              </Link>
              <Link to="/reports" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <BarChart3 className="h-5 w-5" />
                {t('reports')}
              </Link>
              <Link to="/receipts" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <ImageIcon className="h-5 w-5" />
                {t('receipts')}
              </Link>
              <Link to="/import" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <Upload className="h-5 w-5" />
                {t('importItau')}
              </Link>
              <Link to="/settings" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                <Settings className="h-5 w-5" />
                {t('settings')}
              </Link>
              {isAdmin && (
                <Link to="/admin" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors" onClick={() => setMobileMenuOpen(false)}>
                  <ShieldCheck className="h-5 w-5" />
                  Admin Panel
                </Link>
              )}
            </nav>
            <div className="absolute bottom-6 left-6 right-6">
              <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700 mb-4">
                <p className="text-sm text-zinc-400">Conectado como</p>
                <p className="font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
              </div>
              <Button variant="ghost" className="w-full justify-start text-zinc-400 hover:text-red-400" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                {t('logout')}
              </Button>
            </div>
          </aside>
        </div>
      )}

      <aside className="w-64 sidebar border-r border-zinc-800 p-6 hidden lg:block relative min-h-screen">
        <div className="mb-8">
          <h1 className="text-2xl font-black gradient-text">WSFinances</h1>
          <p className="text-zinc-500 text-sm mt-1">Panel de Finanzas</p>
        </div>
        <nav className="space-y-2">
          <Link to="/dashboard" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors">
            <LayoutDashboard className="h-5 w-5" />
            Dashboard
          </Link>
          <Link to="/transactions" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-violet-500/10 text-violet-400 border border-violet-500/20">
            <Receipt className="h-5 w-5" />
            {t('transactions')}
          </Link>
          <Link to="/accounts" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors">
            <Wallet className="h-5 w-5" />
            {t('accounts')}
          </Link>
          <Link to="/categories" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors">
            <Tag className="h-5 w-5" />
            {t('category')}
          </Link>
          <Link to="/recurring" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors">
            <Repeat className="h-5 w-5" />
            Recurrentes
          </Link>
          <Link to="/budgets" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors">
            <Target className="h-5 w-5" />
            {t('budgets')}
          </Link>
          <Link to="/goals" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors">
            <PiggyBank className="h-5 w-5" />
            {t('goalsAndDebts')}
          </Link>
          <Link to="/reports" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors">
            <BarChart3 className="h-5 w-5" />
            {t('reports')}
          </Link>
          <Link to="/receipts" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors">
            <ImageIcon className="h-5 w-5" />
            {t('receipts')}
          </Link>
          <Link to="/import" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors">
            <Upload className="h-5 w-5" />
            {t('importItau')}
          </Link>
          <Link to="/settings" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors">
            <Settings className="h-5 w-5" />
            {t('settings')}
          </Link>
          {isAdmin && (
            <Link to="/admin" className="flex items-center gap-3 px-4 py-3 rounded-lg text-zinc-400 hover:bg-zinc-800/50 transition-colors">
              <ShieldCheck className="h-5 w-5" />
              Admin Panel
            </Link>
          )}
        </nav>
        <div className="absolute bottom-6 left-6 right-6">
          <div className="p-4 rounded-lg bg-zinc-800/50 border border-zinc-700 mb-4">
            <p className="text-sm text-zinc-400">Conectado como</p>
            <p className="font-medium text-white truncate">{user?.name}</p>
          </div>
          <Button variant="ghost" className="w-full justify-start text-zinc-400 hover:text-red-400" onClick={handleLogout}>
            <LogOut className="h-4 w-4 mr-2" />
            {t('logout')}
          </Button>
        </div>
      </aside>

      <main className="flex-1 p-6 lg:p-8 overflow-auto">
        <div className="flex flex-col gap-4 mb-8 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
          <button className="lg:hidden text-zinc-400 hover:text-white" onClick={() => setMobileMenuOpen(true)}>
            <Menu className="h-6 w-6" />
          </button>
          <div>
            <h2 className="text-3xl font-bold text-white">{t('transactions')}</h2>
            <p className="text-zinc-400">{t('transactionHistory')}</p>
          </div>
          </div>
          <Button
            type="button"
            onClick={openExchangeDialog}
            className="bg-emerald-600 hover:bg-emerald-700 text-white sm:self-auto"
            data-testid="currency-exchange-btn"
          >
            <DollarSign className="h-4 w-4 mr-2" />
            Cambio de moneda
          </Button>
        </div>

        {/* Filters */}
        <Card className="bg-zinc-900/50 border-zinc-800 mb-6">
          <CardContent className="p-4 space-y-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-3 h-4 w-4 text-zinc-500" />
                <Input
                  placeholder={t('searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-zinc-950 border-zinc-800"
                  data-testid="search-input"
                />
              </div>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-full sm:w-48 bg-zinc-950 border-zinc-800" data-testid="type-filter">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder={t('filterByType')} />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="all">{t('all')}</SelectItem>
                  <SelectItem value="income">{t('income')}</SelectItem>
                  <SelectItem value="expense">{t('expense')}</SelectItem>
                  <SelectItem value="transfer">{t('transfer')}</SelectItem>
                </SelectContent>
              </Select>
              {availableTags.length > 0 && (
                <Select value={tagFilter} onValueChange={setTagFilter}>
                  <SelectTrigger className="w-full sm:w-48 bg-zinc-950 border-zinc-800" data-testid="tag-filter">
                    <Tag className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Etiqueta" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 max-h-72">
                    <SelectItem value="all">Todas las etiquetas</SelectItem>
                    {availableTags.map(t => (
                      <SelectItem key={t} value={t}>#{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
              <div className="flex-1">
                <Label className="text-zinc-400 text-xs mb-2 block">Desde</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      data-testid="filter-from-trigger"
                      className={cn(
                        "w-full justify-start text-left font-normal bg-zinc-950 border-zinc-800 hover:bg-zinc-800 text-white",
                        !fromDate && "text-zinc-400"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {fromDate ? format(fromDate, 'PPP', { locale: es }) : 'Fecha inicial'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-700 text-white" align="start">
                    <CalendarPicker
                      mode="single"
                      selected={fromDate}
                      onSelect={setFromDate}
                      locale={es}
                      className="bg-zinc-900 text-white"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="flex-1">
                <Label className="text-zinc-400 text-xs mb-2 block">Hasta</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      data-testid="filter-to-trigger"
                      className={cn(
                        "w-full justify-start text-left font-normal bg-zinc-950 border-zinc-800 hover:bg-zinc-800 text-white",
                        !toDate && "text-zinc-400"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {toDate ? format(toDate, 'PPP', { locale: es }) : 'Fecha final'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-700 text-white" align="start">
                    <CalendarPicker
                      mode="single"
                      selected={toDate}
                      onSelect={setToDate}
                      locale={es}
                      className="bg-zinc-900 text-white"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              {(fromDate || toDate) && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={clearDateRange}
                  data-testid="clear-date-range"
                  className="text-zinc-400 hover:text-white"
                >
                  <XCircle className="h-4 w-4 mr-2" />
                  Limpiar fechas
                </Button>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={applyThisMonth}
                data-testid="shortcut-this-month"
                className="bg-zinc-950 border-zinc-800 text-zinc-300 hover:bg-violet-500/10 hover:text-violet-300 hover:border-violet-500/40"
              >
                Este mes
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={applyLastMonth}
                data-testid="shortcut-last-month"
                className="bg-zinc-950 border-zinc-800 text-zinc-300 hover:bg-violet-500/10 hover:text-violet-300 hover:border-violet-500/40"
              >
                Mes pasado
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={applyLast7Days}
                data-testid="shortcut-last-7-days"
                className="bg-zinc-950 border-zinc-800 text-zinc-300 hover:bg-violet-500/10 hover:text-violet-300 hover:border-violet-500/40"
              >
                Últimos 7 días
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Totals summary */}
        <Card className="bg-zinc-900/50 border-zinc-800 mb-6" data-testid="totals-summary">
          <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <p className="text-xs uppercase tracking-wider text-zinc-500">{t('income')}</p>
              <p className="text-2xl font-bold text-emerald-400 font-mono" data-testid="total-income">
                +{formatCurrency(totals.income)}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-zinc-500">{t('expense')}</p>
              <p className="text-2xl font-bold text-rose-400 font-mono" data-testid="total-expense">
                -{formatCurrency(totals.expense)}
              </p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-zinc-500">Total filtrado</p>
              <p
                className={`text-2xl font-bold font-mono ${totals.net >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}
                data-testid="total-net"
              >
                {totals.net >= 0 ? '+' : '-'}{formatCurrency(Math.abs(totals.net))}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Transactions Table */}
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="transactions-table">
          <CardHeader>
            <CardTitle className="text-white">
              {filteredTransactions.length} {t('transactions').toLowerCase()}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800">
                  <TableHead className="text-zinc-400">{t('type')}</TableHead>
                  <TableHead className="text-zinc-400">{t('category')}</TableHead>
                  <TableHead className="text-zinc-400">{t('description')}</TableHead>
                  <TableHead className="text-zinc-400">{t('accounts')}</TableHead>
                  <TableHead className="text-zinc-400">{t('date')}</TableHead>
                  <TableHead className="text-zinc-400 text-right">{t('amount')}</TableHead>
                  <TableHead className="text-zinc-400 text-right">{t('actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTransactions.map((tx) => {
                  const txType = normalizeTransactionType(tx.type);
                  const meta = typeMeta(txType);
                  const TypeIcon = meta.icon;
                  const txCurrency = accountMap[tx.account_id]?.currency || currency;
                  const isCurrencyExchange = (tx.tags || []).includes('currency_exchange');
                  return (
                  <TableRow key={tx.id} className="border-zinc-800" data-testid={`tx-row-${tx.id}`}>
                    <TableCell>
                      <div className={`inline-flex items-center gap-2 px-2 py-1 rounded-md ${meta.badge}`}>
                        <TypeIcon className="h-3 w-3" />
                        <span className="text-xs font-medium">
                          {meta.label}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-white font-medium">
                      {tx.category}
                      {isCurrencyExchange && (
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/25">
                          FX
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-zinc-400">
                      <span>{tx.description || '-'}</span>
                      {tx.installments_total > 1 && (
                        <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300">
                          {tx.installments_current}/{tx.installments_total}
                        </span>
                      )}
                      {tx.receipt_base64 && (
                        <span
                          className="ml-2 inline-flex items-center text-violet-300"
                          title="Tiene recibo adjunto"
                          data-testid={`receipt-badge-${tx.id}`}
                        >
                          <Paperclip className="h-3 w-3" />
                        </span>
                      )}
                      {Array.isArray(tx.tags) && tx.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1" data-testid={`tags-row-${tx.id}`}>
                          {tx.tags.map((tag, i) => (
                            <button
                              key={i}
                              type="button"
                              onClick={() => setTagFilter(tag)}
                              className="text-[10px] bg-violet-500/15 text-violet-300 border border-violet-500/30 px-1.5 py-0.5 rounded-full hover:bg-violet-500/30 transition"
                              title={`Filtrar por #${tag}`}
                            >
                              #{tag}
                            </button>
                          ))}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-zinc-400 text-sm">
                      {txType === 'transfer' ? (
                        <span className="inline-flex items-center gap-1.5">
                          <span style={{ color: accountMap[tx.account_id]?.color || '#a78bfa' }}>
                            {accountMap[tx.account_id]?.name || t('fromAccount')}
                          </span>
                          <ArrowLeftRight className="h-3 w-3 text-zinc-500" />
                          <span style={{ color: accountMap[tx.to_account_id]?.color || '#7dd3fc' }}>
                            {accountMap[tx.to_account_id]?.name || t('toAccount')}
                          </span>
                        </span>
                      ) : tx.account_id && accountMap[tx.account_id]
                        ? <span style={{ color: accountMap[tx.account_id].color || '#a78bfa' }}>
                            {accountMap[tx.account_id].name}
                          </span>
                        : <span className="text-zinc-600">—</span>}
                    </TableCell>
                    <TableCell className="text-zinc-500">{tx.date}</TableCell>
                    <TableCell className={`text-right font-mono font-medium ${meta.amountClass}`}>
                      {meta.sign}{formatCurrencyFor(tx.amount, txCurrency)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-zinc-500 hover:text-violet-400"
                          onClick={() => openEdit(tx)}
                          disabled={isCurrencyExchange}
                          data-testid={`edit-tx-${tx.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-zinc-500 hover:text-red-400"
                          onClick={() => handleDelete(tx.id)}
                          data-testid={`delete-tx-${tx.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )})}
                {filteredTransactions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-zinc-500 py-12">
                      {t('noTransactionsToShow')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </main>

      {/* Currency exchange dialog */}
      <Dialog open={exchangeOpen} onOpenChange={setExchangeOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-h-[90vh] overflow-y-auto" data-testid="currency-exchange-dialog">
          <DialogHeader>
            <DialogTitle>Cambio de moneda</DialogTitle>
            <DialogDescription>
              Registra una compra de moneda extranjera con IOF y spread separados.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveExchange} className="space-y-4">
            {exchangeError && (
              <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
                {exchangeError}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Cuenta origen</Label>
                <Select
                  value={exchangeForm.from_account_id || 'none'}
                  onValueChange={(v) => setExchangeForm({
                    ...exchangeForm,
                    from_account_id: v === 'none' ? '' : v,
                    to_account_id: v === exchangeForm.to_account_id ? '' : exchangeForm.to_account_id
                  })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue placeholder="Itaú BRL" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Seleccionar cuenta</SelectItem>
                    {accounts.filter(acc => acc.type !== 'credit_card').map(acc => (
                      <SelectItem key={acc.id} value={acc.id}>
                        {acc.name} · {acc.currency || currency}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Cuenta destino</Label>
                <Select
                  value={exchangeForm.to_account_id || 'none'}
                  onValueChange={(v) => setExchangeForm({
                    ...exchangeForm,
                    to_account_id: v === 'none' ? '' : v
                  })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700">
                    <SelectValue placeholder="Nomad USD" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Seleccionar cuenta</SelectItem>
                    {accounts.filter(acc => acc.type !== 'credit_card' && acc.id !== exchangeForm.from_account_id).map(acc => (
                      <SelectItem key={acc.id} value={acc.id}>
                        {acc.name} · {acc.currency || currency}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Valor debitado</Label>
                <Input
                  type="number"
                  step="0.01"
                  inputMode="decimal"
                  value={exchangeForm.source_amount}
                  onChange={(e) => setExchangeForm({ ...exchangeForm, source_amount: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="5200.00"
                  required
                />
                <p className="text-xs text-zinc-500 mt-1">
                  Total que salió de {accountMap[exchangeForm.from_account_id]?.name || 'la cuenta origen'}.
                </p>
              </div>
              <div>
                <Label>Valor recibido</Label>
                <Input
                  type="number"
                  step="0.01"
                  inputMode="decimal"
                  value={exchangeForm.target_amount}
                  onChange={(e) => setExchangeForm({ ...exchangeForm, target_amount: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="1000.00"
                  required
                />
                <p className="text-xs text-zinc-500 mt-1">
                  Total que entró en {accountMap[exchangeForm.to_account_id]?.name || 'la cuenta destino'}.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>IOF</Label>
                <Input
                  type="number"
                  step="0.01"
                  inputMode="decimal"
                  value={exchangeForm.iof_amount}
                  onChange={(e) => setExchangeForm({ ...exchangeForm, iof_amount: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>Spread / tarifa</Label>
                <Input
                  type="number"
                  step="0.01"
                  inputMode="decimal"
                  value={exchangeForm.spread_amount}
                  onChange={(e) => setExchangeForm({ ...exchangeForm, spread_amount: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="0.00"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Fecha</Label>
                <Input
                  type="date"
                  value={exchangeForm.date}
                  onChange={(e) => setExchangeForm({ ...exchangeForm, date: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                  required
                />
              </div>
              <div>
                <Label>Descripción</Label>
                <Input
                  value={exchangeForm.description}
                  onChange={(e) => setExchangeForm({ ...exchangeForm, description: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="Nomad USD"
                />
              </div>
            </div>

            {Number(exchangeForm.source_amount || 0) > 0 && Number(exchangeForm.target_amount || 0) > 0 && (
              <div className="rounded-lg border border-zinc-800 bg-zinc-950/50 p-3 text-sm text-zinc-300">
                <div className="flex justify-between gap-3">
                  <span>Tasa efectiva</span>
                  <span className="font-mono">
                    {(Number(exchangeForm.source_amount || 0) / Number(exchangeForm.target_amount || 1)).toFixed(6)}
                  </span>
                </div>
                <div className="flex justify-between gap-3 text-zinc-500 mt-1">
                  <span>Valor convertido sin IOF/spread</span>
                  <span className="font-mono">
                    {formatCurrencyFor(
                      Math.max(0, Number(exchangeForm.source_amount || 0) - Number(exchangeForm.iof_amount || 0) - Number(exchangeForm.spread_amount || 0)),
                      accountMap[exchangeForm.from_account_id]?.currency || currency
                    )}
                  </span>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setExchangeOpen(false)} className="border-zinc-700">
                Cancelar
              </Button>
              <Button type="submit" disabled={exchangeSaving} className="bg-emerald-600 hover:bg-emerald-700">
                {exchangeSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <DollarSign className="h-4 w-4 mr-2" />}
                Guardar cambio
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editing} onOpenChange={(open) => !open && closeEdit()}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-h-[90vh] overflow-y-auto" data-testid="edit-transaction-dialog">
          <DialogHeader>
            <DialogTitle>Editar Transacción</DialogTitle>
            <DialogDescription>Modifica los datos de la transacción</DialogDescription>
          </DialogHeader>
          {editForm && (
            <form onSubmit={handleSaveEdit} className="space-y-4">
              <div>
                <Label>{t('type')}</Label>
                <Select
                  value={editForm.type}
                  onValueChange={(v) => {
                    setEditError('');
                    setEditForm({
                      ...editForm,
                      type: v,
                      category: v === 'transfer' ? TRANSFER_CATEGORY : '',
                      to_account_id: v === 'transfer' ? editForm.to_account_id : ''
                    });
                  }}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="edit-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="income">{t('income')}</SelectItem>
                    <SelectItem value="expense">{t('expense')}</SelectItem>
                    <SelectItem value="transfer">{t('transfer')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {editForm.type !== 'transfer' && (
              <div>
                <Label>{t('category')}</Label>
                <Select
                  value={editForm.category}
                  onValueChange={(v) => setEditForm({ ...editForm, category: v })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="edit-category">
                    <SelectValue placeholder="Seleccionar categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    {editCategories.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              )}
              <div>
                <Label>{t('amount')}</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editForm.amount}
                  onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                  required
                  data-testid="edit-amount"
                />
              </div>
              <div>
                <Label>{t('description')}</Label>
                <Input
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="Descripción opcional"
                  data-testid="edit-description"
                />
              </div>
              <div>
                <Label htmlFor="edit-account">{editForm.type === 'transfer' ? t('fromAccount') : t('accounts')}</Label>
                <Select
                  value={editForm.account_id || 'none'}
                  onValueChange={(v) => {
                    const accountId = v === 'none' ? '' : v;
                    setEditError('');
                    setEditForm({
                      ...editForm,
                      account_id: accountId,
                      to_account_id: accountId && accountId === editForm.to_account_id ? '' : editForm.to_account_id
                    });
                  }}
                >
                  <SelectTrigger id="edit-account" className="bg-zinc-800 border-zinc-700" data-testid="edit-account-trigger">
                    <SelectValue placeholder={t('noLinkedAccount')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('noLinkedAccount')}</SelectItem>
                    {accounts.map(acc => (
                      <SelectItem key={acc.id} value={acc.id}>
                        {acc.name}{accountTypeSuffix(acc.type)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {editForm.type === 'transfer' && (
                <div>
                  <Label htmlFor="edit-to-account">{t('toAccount')}</Label>
                  <Select
                    value={editForm.to_account_id || 'none'}
                    onValueChange={(v) => {
                      setEditError('');
                      setEditForm({ ...editForm, to_account_id: v === 'none' ? '' : v });
                    }}
                  >
                    <SelectTrigger id="edit-to-account" className="bg-zinc-800 border-zinc-700" data-testid="edit-to-account-trigger">
                      <SelectValue placeholder={t('selectAccount')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t('selectAccount')}</SelectItem>
                      {accounts.map(acc => (
                        <SelectItem key={acc.id} value={acc.id} disabled={acc.id === editForm.account_id}>
                          {acc.name}{accountTypeSuffix(acc.type)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {editError && (
                <div
                  className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300"
                  data-testid="edit-error"
                >
                  {editError}
                </div>
              )}
              {!editError && editTransferInvalid && (
                <p className="text-xs text-amber-300" data-testid="edit-transfer-help">
                  Para convertir en transferencia selecciona una cuenta origen y una cuenta destino diferente.
                </p>
              )}
              <div>
                <Label htmlFor="edit-notes">Notas</Label>
                <Input
                  id="edit-notes"
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="Notas opcionales"
                />
              </div>
              <div>
                <Label htmlFor="edit-tags">Etiquetas (separadas por coma)</Label>
                <Input
                  id="edit-tags"
                  value={editForm.tags}
                  onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })}
                  className="bg-zinc-800 border-zinc-700"
                  placeholder="ej: viaje, trabajo, urgente"
                  data-testid="edit-tags-input"
                />
                {editForm.tags && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {editForm.tags.split(',').map(t => t.trim()).filter(Boolean).map((tag, i) => (
                      <span key={i} className="text-xs bg-violet-500/15 text-violet-300 border border-violet-500/30 px-2 py-0.5 rounded-full">
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <Label className="mb-2 block">Recibo</Label>
                <ReceiptUploader
                  value={editForm.receipt_base64}
                  onChange={(v) => setEditForm({ ...editForm, receipt_base64: v })}
                  testId="edit-receipt"
                />
              </div>
              <div className="flex flex-col">
                <Label className="mb-2">Fecha</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      data-testid="edit-date-trigger"
                      className={cn(
                        "w-full justify-start text-left font-normal bg-zinc-800 border-zinc-700 hover:bg-zinc-700 text-white",
                        !editForm.date && "text-zinc-400"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {editForm.date ? format(editForm.date, 'PPP', { locale: es }) : 'Selecciona una fecha'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-700 text-white" align="start">
                    <CalendarPicker
                      mode="single"
                      selected={editForm.date}
                      onSelect={(d) => d && setEditForm({ ...editForm, date: d })}
                      locale={es}
                      className="bg-zinc-900 text-white"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={closeEdit} className="text-zinc-300">
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="bg-violet-600 hover:bg-violet-700"
                  disabled={saving}
                  data-testid="edit-save"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Guardar cambios'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TransactionsPage;
